<script setup>
import { computed } from "vue";

import { AnswerStateEnum } from "@/common/composables/use-vocab-quiz";
import IconCorrect from "@/components/icons/IconCorrect.vue";
import IconIncorrect from "@/components/icons/IconIncorrect.vue";
import { i18n } from "@/i18n";

const props = defineProps({
  state: {
    type: Number,
    required: true
  },
  textId: {
    type: String,
    default: undefined
  },
  text: {
    type: String,
    required: true
  },
  lang: {
    type: String,
    required: true
  }
});

const { t } = i18n.global;

const isHidden = computed(() => props.state === AnswerStateEnum.Hidden);

const stateClass = computed(() => {
  const baseClass = "quiz-selector-button";

  if (props.state === AnswerStateEnum.Correct) {
    return `${baseClass}--correct`;
  }

  if (props.state === AnswerStateEnum.Incorrect) {
    return `${baseClass}--incorrect`;
  }

  if (props.state === AnswerStateEnum.Pending) {
    return `${baseClass}--pending`;
  }

  if (props.state === AnswerStateEnum.Hidden) {
    return `${baseClass}--hidden`;
  }

  return "";
});
</script>

<template>
  <button
    :aria-hidden="isHidden ? true : undefined"
    :tabindex="isHidden ? '-1' : undefined"
    class="quiz-selector-button"
    :class="stateClass"
  >
    <div class="quiz-selector-button__content-container">
      <span :id="textId" :lang="lang" class="quiz-selector-button__text">
        {{ text }}
        <slot />
      </span>

      <template v-if="state === AnswerStateEnum.Correct">
        <IconCorrect class="quiz-selector-button__icon quiz-selector-button__icon--correct" />
        <span class="sr-only" aria-live="polite">
          {{ t("vocab.quizSelectorButton.correct") }}
        </span>
      </template>
      <template v-else-if="state === AnswerStateEnum.Incorrect">
        <IconIncorrect class="quiz-selector-button__icon quiz-selector-button__icon--incorrect" />
        <span class="sr-only" aria-live="polite">
          {{ t("vocab.quizSelectorButton.incorrect") }}
        </span>
      </template>
      <template v-else-if="state === AnswerStateEnum.Pending">
        <span class="sr-only" aria-live="polite">
          {{ t("vocab.quizSelectorButton.pending") }}
        </span>
      </template>
    </div>
  </button>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@icon-size: 38px;

.quiz-selector-button {
  width: 100%;
  padding: 17px 20px 18px 20px;
  transition: 100ms all ease-in-out;
  border: 1px solid rgb(@theme-colors[@grey][50]);
  border-radius: 4px;
  background: rgb(@theme-colors[@grey][10]);
  color: @colors[@black];
  word-break: break-word;
  hyphens: auto;

  &:hover:not(:disabled) {
    border-color: rgb(@theme-colors[@grey][50]);
    background: rgb(@theme-colors[@grey][50]);
  }

  &:active:not(:disabled) {
    border-color: rgb(@theme-colors[@grey][base]);
  }

  &--correct {
    border-color: rgb(@theme-colors[@primary][base]);
    background: rgb(@theme-colors[@primary][base]);
    color: @colors[@white];
  }

  &--incorrect {
    border-color: rgb(@theme-colors[@red][base]);
    background: rgb(@theme-colors[@red][10]);
    color: rgb(@theme-colors[@red][base]);
  }

  &--pending {
    border-color: rgb(@theme-colors[@secondary][base]);
    background: rgb(@theme-colors[@secondary][base]);
    color: @colors[@white];

    &:hover:not(:disabled) {
      border-color: rgb(@theme-colors[@secondary][110]);
      background: rgb(@theme-colors[@secondary][110]);
    }
  }

  &--hidden {
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
  }
}
.quiz-selector-button__content-container {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  min-height: @icon-size;
  column-gap: 6px;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.75rem;
  justify-items: center;
}

.quiz-selector-button__text {
  grid-column-start: 2;
}

.quiz-selector-button__icon {
  width: @icon-size;
  height: @icon-size;
  margin-left: auto;
}

.quiz-selector-button__icon--correct {
  color: rgb(@theme-colors[@primary][50]);
}

.quiz-selector-button__icon--incorrect {
  color: rgb(@theme-colors[@red][base]);
}
</style>
